/**
 * /accounts — Chart of Accounts admin.
 *
 * DataGridPro list with: code · name · type · normal balance · postable ·
 * active · system role · row actions (edit / delete).
 *
 * Toolbar: filter by type, hide-inactive toggle, "+ New account", "Import CSV".
 * Delete refuses (server-side) accounts with journal_line history → use the
 * Active toggle instead (which is also what the edit dialog exposes).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DataGridPro,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { gridDefaults, makeGroupable } from "@/grids/gridDefaults";
import { Box, Chip } from "@mui/material";

import { api } from "@/lib/api";
import {
  accountKeys,
  accountsFullListQueryOptions,
} from "@/lib/queries";
import { AccountFormDialog } from "@/components/AccountFormDialog";
import { AccountImportDialog } from "@/components/AccountImportDialog";
import { useConfirmAction } from "@/components/ConfirmDialog";
import { useToast } from "@/components/Toaster";
import type { Account, AccountType } from "@/lib/types";
import { codeAndName, codeFor } from "../lib/displayCode";

export const Route = createFileRoute("/_auth/accounts")({
  component: AccountsPage,
});

const TYPE_COLOR: Record<AccountType, string> = {
  asset: "#2563eb",
  liability: "#9333ea",
  equity: "#0891b2",
  revenue: "#16a34a",
  expense: "#dc2626",
};

function AccountsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirmAction();

  const [showInactive, setShowInactive] = useState(false);
  const [typeFilter, setTypeFilter] = useState<AccountType | "">("");
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Account | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const accountsQuery = useQuery(
    accountsFullListQueryOptions({ active_only: !showInactive }),
  );

  const filteredRows = useMemo(() => {
    let rows = accountsQuery.data ?? [];
    if (typeFilter) rows = rows.filter((a) => a.account_type === typeFilter);
    return rows;
  }, [accountsQuery.data, typeFilter]);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api(`/api/accounts/${id}`, { method: "DELETE" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: accountKeys.all });
      toast.success("Account deleted");
    },
    onError: (err: Error) => toast.error(`Delete failed: ${err.message}`),
  });

  const requestDelete = (a: Account) => {
    confirmAction({
      title: `Delete account ${codeAndName(a)}?`,
      message:
        `${codeAndName(a, " — ")}\n\n` +
        `Hard delete is refused if any journal line references this account ` +
        `(409 from the server). For accounts with history, use the Active toggle ` +
        `in the edit dialog instead — that hides them from dropdowns without ` +
        `breaking the audit trail.`,
      confirmLabel: "Delete",
      kind: "danger",
      onConfirm: async () => { await deleteMutation.mutateAsync(a.id); },
    });
  };

  const columns = useMemo<GridColDef<Account>[]>(
    () => [
      {
        field: "code",
        headerName: "Code",
        width: 110,
        renderCell: (p: GridRenderCellParams<Account>) => (
          <Box
            component="span"
            sx={{
              fontFamily: "monospace",
              fontWeight: 600,
              color: TYPE_COLOR[p.row.account_type],
            }}
          >
            {codeFor(p.row)}
          </Box>
        ),
      },
      { field: "name", headerName: "Name", flex: 2, minWidth: 240 },
      {
        field: "account_type",
        headerName: "Type",
        width: 100,
        renderCell: (p: GridRenderCellParams<Account>) => (
          <Box
            component="span"
            sx={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: TYPE_COLOR[p.value as AccountType],
              fontWeight: 600,
            }}
          >
            {p.value as string}
          </Box>
        ),
      },
      {
        field: "normal_balance",
        headerName: "Normal",
        width: 90,
        renderCell: (p) => (
          <Chip
            label={p.value as string}
            size="small"
            variant="outlined"
            sx={{ fontSize: 10, height: 18 }}
          />
        ),
      },
      {
        field: "is_postable",
        headerName: "Postable",
        width: 100,
        type: "boolean",
        renderCell: (p) =>
          p.value ? (
            <Chip size="small" label="postable" color="success" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
          ) : (
            <Chip size="small" label="header" color="default" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
          ),
      },
      {
        field: "system_role",
        headerName: "System role",
        width: 150,
        renderCell: (p) =>
          p.value && p.value !== "none" ? (
            <Chip size="small" label={p.value as string} color="warning" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
          ) : (
            <Box sx={{ color: "text.disabled" }}>—</Box>
          ),
      },
      {
        field: "currency_code",
        headerName: "Ccy",
        width: 70,
        renderCell: (p) => p.value ? <Box sx={{ fontFamily: "monospace" }}>{p.value as string}</Box> : <Box sx={{ color: "text.disabled" }}>—</Box>,
      },
      {
        field: "is_active",
        headerName: "Active",
        width: 90,
        type: "boolean",
        renderCell: (p) => (
          <Chip
            size="small"
            label={p.value ? "active" : "inactive"}
            color={p.value ? "success" : "default"}
            variant={p.value ? "filled" : "outlined"}
            sx={{ fontSize: 10, height: 18 }}
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams<Account>) => (
          <div className="flex gap-1">
            <button
              type="button"
              title="Edit"
              onClick={() => { setEditTarget(p.row); setFormOpen(true); }}
              className="text-slate-500 hover:text-blue-700 px-1 dark:text-slate-400"
            >
              ✎
            </button>
            <button
              type="button"
              title="Delete"
              onClick={() => requestDelete(p.row)}
              className="text-slate-500 hover:text-red-600 px-1 dark:text-slate-400"
            >
              ✕
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Chart of Accounts</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {accountsQuery.data?.length ?? 0} accounts
            {!showInactive ? " (active only)" : ""}
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md px-3 py-1.5 font-medium dark:bg-gray-800 dark:text-slate-300"
            title="Bulk-import accounts from a CSV"
          >
            📥 Import CSV
          </button>
          <button
            type="button"
            onClick={() => { setEditTarget(null); setFormOpen(true); }}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            + New account
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-end gap-4 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 dark:text-slate-400">Type</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as AccountType | "")}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
          >
            <option value="">all types</option>
            {(["asset", "liability", "equity", "revenue", "expense"] as const).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 ml-1 mb-1 dark:text-slate-300">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          show inactive
        </label>
        <div className="text-xs text-slate-500 ml-auto mb-1 dark:text-slate-400">
          {filteredRows.length} of {accountsQuery.data?.length ?? 0} matching filters
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 px-6 py-4 overflow-hidden">
        {accountsQuery.error && (
          <div className="text-sm text-red-600">Error: {(accountsQuery.error as Error).message}</div>
        )}
        <Box sx={{ height: "100%", width: "100%" }}>
          <DataGridPro
            {...gridDefaults}
            rows={filteredRows}
            columns={makeGroupable(columns)}
            getRowId={(r) => r.id}
            loading={accountsQuery.isLoading}
            disableRowSelectionOnClick
            density="compact"
            pinnedColumns={{ left: ["code", "name"] }}
            sx={{
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "action.hover",
                fontSize: 11,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "text.secondary",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
            }}
          />
        </Box>
      </div>

      {formOpen && (
        <AccountFormDialog
          open={formOpen}
          mode={editTarget ? { kind: "edit", account: editTarget } : { kind: "create" }}
          onClose={() => { setFormOpen(false); setEditTarget(null); }}
        />
      )}
      <AccountImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}
